import React, { useState, useCallback, useMemo } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { WarehouseFormModal } from '@components/organisms/WarehouseFormModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import {
  useWarehouses,
  useCreateWarehouse,
  useUpdateWarehouse,
  useSetDefaultWarehouse,
  useDeleteWarehouse,
} from '@hooks/api/warehouse';
import {
  Warehouse,
  WarehouseCreateInput,
  WarehouseUpdateInput,
} from '@models/warehouse.model';
import AddIcon from '@mui/icons-material/Add';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import WarehouseGeofenceModal from '@components/organisms/WarehouseGeofenceModal';

const WarehouseManagementPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [warehouseToDelete, setWarehouseToDelete] = useState<Warehouse | null>(null);
  const [warehouseToSetDefault, setWarehouseToSetDefault] = useState<Warehouse | null>(null);
  const [warehouseGeofenceTarget, setWarehouseGeofenceTarget] = useState<Warehouse | null>(null);

  // API Hooks
  const {
    data: warehouses = [],
    isLoading: warehousesLoading,
    isError: warehousesError,
  } = useWarehouses();

  // Mutations
  const createMutation = useCreateWarehouse();
  const updateMutation = useUpdateWarehouse();
  const setDefaultMutation = useSetDefaultWarehouse();
  const deleteMutation = useDeleteWarehouse();

  const isLoading = warehousesLoading && !warehousesError;

  // Filter warehouses by search
  const filteredWarehouses = useMemo(() => {
    if (!searchQuery.trim()) return warehouses;
    const query = searchQuery.toLowerCase();
    return warehouses.filter(
      (w) =>
        w.name.toLowerCase().includes(query) ||
        w.description?.toLowerCase().includes(query) ||
        w.address?.toLowerCase().includes(query)
    );
  }, [warehouses, searchQuery]);

  // Stats
  const activeCount = warehouses.filter((w) => w.isActive).length;
  const defaultWarehouse = warehouses.find((w) => w.isDefault);

  // Handlers
  const handleAddWarehouse = useCallback(() => {
    setSelectedWarehouse(null);
    setIsFormModalOpen(true);
  }, []);

  const handleEditWarehouse = useCallback((warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsFormModalOpen(true);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedWarehouse(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (data: WarehouseCreateInput | WarehouseUpdateInput) => {
      try {
        if ('warehouseId' in data && data.warehouseId) {
          await updateMutation.mutateAsync(data as WarehouseUpdateInput);
        } else {
          await createMutation.mutateAsync(data as WarehouseCreateInput);
        }
        handleCloseFormModal();
      } catch (error) {
        // Hooks handle toasts
        console.error('Error saving warehouse:', error);
      }
    },
    [createMutation, updateMutation, handleCloseFormModal]
  );

  const handleDeleteWarehouse = useCallback((warehouse: Warehouse) => {
    setWarehouseToDelete(warehouse);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!warehouseToDelete) return;
    try {
      await deleteMutation.mutateAsync(warehouseToDelete.warehouseId);
      setWarehouseToDelete(null);
    } catch (error) {
      console.error('Error deleting warehouse:', error);
    }
  }, [warehouseToDelete, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setWarehouseToDelete(null);
  }, []);

  const handleSetDefault = useCallback((warehouse: Warehouse) => {
    setWarehouseToSetDefault(warehouse);
  }, []);

  const handleConfirmSetDefault = useCallback(async () => {
    if (!warehouseToSetDefault) return;
    try {
      await setDefaultMutation.mutateAsync(warehouseToSetDefault.warehouseId);
      setWarehouseToSetDefault(null);
    } catch (error) {
      console.error('Error setting default warehouse:', error);
    }
  }, [warehouseToSetDefault, setDefaultMutation]);

  if (isLoading) {
    return (
      <WarehouseLayout>
        <div className="flex justify-center items-center h-full">
          <Loading size="3xl" />
        </div>
      </WarehouseLayout>
    );
  }

  return (
    <WarehouseLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Warehouses
          </h1>
          <Button
            variant="primary"
            leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddWarehouse}
          >
            Add Warehouse
          </Button>
        </div>

        {/* Quick Stats */}
        {warehouses.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
              <WarehouseIcon className="text-gray-400" sx={{ fontSize: 24 }} />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{warehouses.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Warehouses</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
              <CheckCircleIcon className="text-green-500" sx={{ fontSize: 24 }} />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeCount}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
              <StarIcon className="text-yellow-500" sx={{ fontSize: 24 }} />
              <div>
                <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{defaultWarehouse?.name || 'None'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Default Warehouse</p>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        {warehouses.length > 0 && (
          <div className="relative w-64">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              sx={{ fontSize: 18 }}
            />
            <input
              type="text"
              placeholder="Search warehouses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        )}

        {/* Warehouse Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Add Warehouse Card */}
          <button type="button"
            onClick={handleAddWarehouse}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 transition-colors min-h-[140px]"
          >
            <AddIcon sx={{ fontSize: 24 }} />
            <span className="text-sm font-medium">Add Warehouse</span>
          </button>

          {filteredWarehouses.map((wh) => (
            <div
              key={wh.warehouseId}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 relative group transition-all hover:shadow-md ${
                !wh.isActive ? 'opacity-60' : ''
              }`}
            >
              {/* Default Badge */}
              {wh.isDefault && (
                <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                  <StarIcon sx={{ fontSize: 12 }} />
                  Default
                </div>
              )}

              {/* Icon */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-indigo-100 text-indigo-600 ${wh.isDefault ? 'mt-6' : ''}`}>
                <WarehouseIcon sx={{ fontSize: 22 }} />
              </div>

              {/* Name & Description */}
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{wh.name}</h3>
              {wh.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{wh.description}</p>
              )}

              {/* Address */}
              {wh.address && (
                <div className="flex items-center gap-1 mt-2">
                  <LocationOnIcon className="text-gray-400" sx={{ fontSize: 14 }} />
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{wh.address}</span>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2 mt-3">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    wh.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {wh.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Hover Actions */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button type="button"
                  onClick={() => setWarehouseGeofenceTarget(wh)}
                  className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-orange-600 hover:ring-orange-200"
                  title="View geofence"
                >
                  <PinDropOutlinedIcon sx={{ fontSize: 16 }} />
                </button>
                {!wh.isDefault && (
                  <button type="button"
                    onClick={() => handleSetDefault(wh)}
                    className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-yellow-600 hover:ring-yellow-200"
                    title="Set as Default"
                  >
                    <StarBorderIcon sx={{ fontSize: 16 }} />
                  </button>
                )}
                <button type="button"
                  onClick={() => handleEditWarehouse(wh)}
                  className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-indigo-600 hover:ring-indigo-200"
                  title="Edit"
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </button>
                {!wh.isDefault && (
                  <button type="button"
                    onClick={() => handleDeleteWarehouse(wh)}
                    className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-red-600 hover:ring-red-200"
                    title="Delete"
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </button>
                )}
              </div>
            </div>
          ))}

        </div>
      </div>

      {/* Form Modal */}
      <WarehouseFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={handleSubmitForm}
        warehouse={selectedWarehouse}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!warehouseToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Warehouse"
        description={`Are you sure you want to delete "${warehouseToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Set Default Confirmation */}
      <ConfirmationModal
        isOpen={!!warehouseToSetDefault}
        onClose={() => setWarehouseToSetDefault(null)}
        onConfirm={handleConfirmSetDefault}
        title="Set Default Warehouse"
        description={`Are you sure you want to set "${warehouseToSetDefault?.name}" as the default warehouse? The current default will be changed.`}
        confirmText="Set as Default"
        variant="info"
      />

      <WarehouseGeofenceModal
        isOpen={!!warehouseGeofenceTarget}
        onClose={() => setWarehouseGeofenceTarget(null)}
        warehouse={warehouseGeofenceTarget}
        hideActiveToggle
      />
    </WarehouseLayout>
  );
};

export default WarehouseManagementPage;
